import { Box, Button, Divider, FormControlLabel, Grid, IconButton, LinearProgress, Radio, RadioGroup, Typography, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "../../redux-store/reducers";
import { useEffect, useRef, useState } from "react";
import { FieldArray, Form, Formik, FormikProps, validateYupSchema, yupToFormErrors } from "formik";
import * as Yup from 'yup';
import { TextDatePicker } from "../onboarding/DatePickerWithTextFeild";
import { allowDecimalNumbers, applicationComparison, applyRoleBasedStatus, currencyConversion, isModuleAllowed, saveForLater, toINR } from "../../utils/utilityFunctions";
import { MFTextField } from "../../lib/formik";
import { DeleteIcons } from "../../assets/SvgIcons";
import { ProceedSaveLater } from "./components";
import { nonNegativeNumberRegex } from "../../utils/regex";
import { APPLICATION_TYPE, RIA_FEE_DECLARATION_POINTS, USER_ROLES } from "../../utils/constant";
import dayjs from "../../utils/dayjs";
import { updateApplication } from "../../redux-store/actions/application";
import { showError } from "../../redux-store/actions/auth";
import { useHistory } from "react-router";
import { useSnackbar } from "notistack";
import { getFamilyMembersFeeListData } from "../../redux-store/actions/reports";
import { GetFamilyMembersFeeListDataResponseBody } from "../../redux-store/types/onBoarding";

const FEE_TYPE_VALUES = {
    FIXED_FEE: "FIXED",
    AUA_FEE_VARIABLE: "VARIABLE",
    AUA_FEE_SLAB_WISE: "SLAB"
} as const

const FEE_TYPES: Array<{ label: string; value: string }> = [
    { label: "Fixed fee", value: FEE_TYPE_VALUES.FIXED_FEE },
    { label: "AUA Fee (Variable)", value: FEE_TYPE_VALUES.AUA_FEE_VARIABLE },
    { label: "AUA Fee (Slab Wise)", value: FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE },
]

const MAX_FIXED_FEE_AMOUNT = 151000;
const MAX_FEE_PERCENT = 2.5;
const MAX_DECIMAL_PLACES = 3;

// Checks whether a string is a valid non-negative decimal number.
// Accepts: integers (e.g. "100") and decimals (e.g. "0.45", "3.14")
// Rejects: "", ".", ".45", "23.", and non-numeric input
const isValidDecimalFormat = (value: string): boolean => {
    if (!value || value === "." || value.endsWith(".")) {
        return false;
    }
    // nonNegativeNumberRegex => checks whether the value is a non-negative integer or decimal number
    return !Number.isNaN(Number(value)) && nonNegativeNumberRegex.test(value);
};

// Returns the number of digits after the decimal point
const getDecimalPlaces = (value: string): number =>
    `${value ?? ""}`.split(".")[1]?.length ?? 0;

const getSelectedDeclarationIds = (declarationId: string): { parentId: string; subPointId: string } => {
    if (!declarationId) return { parentId: "", subPointId: "" };

    if (declarationId === "1") {
        return { parentId: "1", subPointId: "" };
    }

    if (declarationId === "2.1" || declarationId === "2.2") {
        return { parentId: "2", subPointId: declarationId };
    }

    return { parentId: "", subPointId: "" };
}

const riaFeeValidationSchema = (locale: string, currencyCode: string) =>
    Yup.object().shape({
        RiaFees: Yup.array().of(
            Yup.object().shape({
                agreementDate: Yup
                    .date()
                    .typeError("Please enter a valid date")
                    .nullable()
                    .required("Effective Agreement Date is required")
                    .test(
                        "not-future-date",
                        "Effective Agreement date cannot be a future date",
                        (value) => {
                            if (!value) return true; // .required() handles empty case
                            const selectedDate = dayjs(value);
                            // Only allow current date or past dates
                            return selectedDate.isValid() && !selectedDate.isAfter(dayjs(), "day");
                        }
                    ),
                feeType: Yup.string(),
                fixedFee: Yup
                    .string()
                    .when("feeType", {
                        is: FEE_TYPE_VALUES.FIXED_FEE,
                        then: () => Yup.string()
                            .required("Annual Fixed Fee Amount is required")
                            .test(
                                "valid-decimal-format",
                                "Please enter a valid number",
                                (value) => isValidDecimalFormat(`${value ?? ""}`.trim())
                            )
                            .test(
                                "max-fixed-fee",
                                `Maximum value allowed is ${toINR({ value: MAX_FIXED_FEE_AMOUNT, locale, currencyCode })}`,
                                function (value) {
                                    const { options } = this;
                                    const applicationType = (options.context as Values)?.applicationType;

                                    if (applicationType === APPLICATION_TYPE.NON_INDIVIDUAL) {
                                        return true;
                                    }

                                    return Number(`${value ?? ""}`.trim()) <= MAX_FIXED_FEE_AMOUNT;
                                }
                            ),
                        otherwise: () => Yup.string(),
                    }),
                variableFee: Yup
                    .string()
                    .when("feeType", {
                        is: FEE_TYPE_VALUES.AUA_FEE_VARIABLE,
                        then: () => Yup.string()
                            .required("Fee percentage P.A is required")
                            .test(
                                "valid-decimal-format",
                                "Please enter a valid number",
                                (value) => isValidDecimalFormat(`${value ?? ""}`.trim())
                            )
                            .test(
                                "max-fee-percent",
                                `Maximum value allowed is ${MAX_FEE_PERCENT}%`,
                                (value) => Number(`${value ?? ""}`.trim()) <= MAX_FEE_PERCENT
                            )
                            .test(
                                "fee-percent-precision",
                                `Maximum ${MAX_DECIMAL_PLACES} decimal places are allowed`,
                                (value) => getDecimalPlaces(`${value ?? ""}`.trim()) <= MAX_DECIMAL_PLACES
                            ),
                        otherwise: () => Yup.string(),
                    }),
                slabs: Yup
                    .array()
                    .when("feeType", {
                        is: FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE,
                        then: () => Yup
                            .array()
                            .of(Yup
                                .object()
                                .shape({
                                    rate: Yup.string()
                                        .required("Fee percentage P.A is required")
                                        .test(
                                            "valid-decimal-format",
                                            "Please enter a valid number",
                                            (value) => isValidDecimalFormat(`${value ?? ""}`.trim())
                                        )
                                        .test(
                                            "max-fee-percent",
                                            `Maximum value allowed is ${MAX_FEE_PERCENT}%`,
                                            (value) => Number(`${value ?? ""}`.trim()) <= MAX_FEE_PERCENT
                                        )
                                        .test(
                                            "fee-percent-precision",
                                            `Maximum ${MAX_DECIMAL_PLACES} decimal places are allowed`,
                                            (value) => getDecimalPlaces(`${value ?? ""}`.trim()) <= MAX_DECIMAL_PLACES
                                        ),
                                    minAum: Yup.string()
                                        .required("Minimum AUM is required")
                                        .test(
                                            "valid-decimal-format",
                                            "Please enter a valid number",
                                            (value) => isValidDecimalFormat(`${value ?? ""}`.trim())
                                        ),
                                    maxAum: Yup.string()
                                        .required("Maximum AUM is required")
                                        .test(
                                            "valid-decimal-format",
                                            "Please enter a valid number",
                                            (value) => isValidDecimalFormat(`${value ?? ""}`.trim())
                                        )
                                        .test(
                                            "max-greater-than-min",
                                            "Max AUM should be greater than Min AUM",
                                            function (value) {
                                                const minAum = `${this.parent?.minAum ?? ""}`.trim();
                                                // Skip comparison if either value is empty or has invalid format
                                                // (required/format validations will handle those cases)
                                                if (!value?.trim() || !minAum) return true;
                                                if (!isValidDecimalFormat(value.trim()) || !isValidDecimalFormat(minAum)) return true;

                                                return Number(value.trim()) > Number(minAum);
                                            }
                                        ),
                                })
                            )
                            .test("no-overlapping-slabs", function (slabs) {
                                const slabsData = slabs ?? [];

                                for (let i = 0; i < slabsData.length; i++) {
                                    const minA = Number(`${slabsData[i]?.minAum ?? ""}`.trim());
                                    const maxA = Number(`${slabsData[i]?.maxAum ?? ""}`.trim());

                                    if (Number.isNaN(minA) || Number.isNaN(maxA)) continue;

                                    for (let j = i + 1; j < slabsData.length; j++) {
                                        const minB = Number(`${slabsData[j]?.minAum ?? ""}`.trim());
                                        const maxB = Number(`${slabsData[j]?.maxAum ?? ""}`.trim());

                                        if (Number.isNaN(minB) || Number.isNaN(maxB)) continue;

                                        // Overlap condition: ranges overlap if minA <= maxB AND minB <= maxA
                                        if (minA <= maxB && minB <= maxA) {
                                            return this.createError({
                                                path: `${this.path}[${j}].maxAum`,
                                                message: `Overlaps with Slab ${i + 1}`,
                                            });
                                        }
                                    }
                                }
                                return true;
                            }),
                        otherwise: () => Yup.array(),
                    }),
            })
        ),
    });

interface SlabObj {
    rate: string;
    minAum: string;
    maxAum: string;
}

interface RiaFeeDataObj {
    agreementDate: string;
    feeType: string;
    applicationId: string | number;
    fixedFee: string;
    variableFee: string;
    slabs: Array<SlabObj>;
    id?: number;
    riaFeeDeclaration: string;
    selectedDeclarationId: string;
    selectedSubPointId: string;
}

interface Values {
    applicationType: string;
    isIndependent: string;
    isHeadOfFamily: string;
    RiaFees: Array<RiaFeeDataObj>;
    saveType: string;
}

const initialRiaFeeObj: RiaFeeDataObj = {
    agreementDate: `${new Date().toISOString()}`,
    feeType: FEE_TYPE_VALUES.FIXED_FEE,
    applicationId: '',
    fixedFee: "",
    variableFee: "",
    slabs: [
        {
            rate: "",
            minAum: "0",
            maxAum: ""
        }
    ],
    riaFeeDeclaration: "",
    selectedDeclarationId: "",
    selectedSubPointId: ""
}

const initialValues: Values = {
    applicationType: '',
    isIndependent: '',
    isHeadOfFamily: '',
    RiaFees: [initialRiaFeeObj],
    saveType: 'save and proceed'
}

export default function RiaFee(): JSX.Element {
    const theme = useTheme();
    const dispatch = useDispatch();
    const history = useHistory();

    const { enqueueSnackbar } = useSnackbar();

    // Data from Redux
    const { application } = useSelector((store: RootStateType) => store.application);
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const { role = '' } = useSelector((store: RootStateType) => store.auth);

    // State
    const [initialFormikValues, setInitialFormikValues] = useState<Values>(initialValues);
    const [isLoading, setLoading] = useState<boolean>(false)
    const [isFeeTypeLoading, setFeeTypeLoading] = useState<boolean>(false)

    const formikRef = useRef<FormikProps<Values>>(null);

    const setFormData = async () => {
        let familyFeeType: string = FEE_TYPE_VALUES.FIXED_FEE;

        if (application?.isHeadOfFamily === "N") {
            try {
                setFeeTypeLoading(true);

                const payload = {
                    year: null,
                    quarter: null,
                    familyId: application?.familyId ?? ""
                };

                const response = await (dispatch(getFamilyMembersFeeListData(payload)) as unknown as Promise<GetFamilyMembersFeeListDataResponseBody>);

                if (!response?.isSuccess) {
                    dispatch(showError(response?.message || "Data not found"))
                }

                if (response?.isSuccess) {
                    familyFeeType = response?.data?.feeType ?? FEE_TYPE_VALUES.FIXED_FEE;
                }
            } catch (error) {
                console.log("Error: ", error);
            } finally {
                setFeeTypeLoading(false);
            }
        }

        setInitialFormikValues({
            ...initialFormikValues,
            applicationType: application?.applicationType ?? "",
            isIndependent: application?.isIndependent ?? "",
            isHeadOfFamily: application?.isHeadOfFamily ?? "",
            RiaFees: (application?.RiaFees ?? []).length ?
                ((application?.RiaFees ?? []).map(obj => {
                    const { parentId, subPointId } = getSelectedDeclarationIds(obj?.riaFeeDeclaration ?? "");

                    return {
                        agreementDate: obj?.agreementDate ?? `${new Date().toISOString()}`,
                        feeType: obj?.feeType ?? FEE_TYPE_VALUES.FIXED_FEE,
                        applicationId: obj?.applicationId ?? "",
                        fixedFee: obj?.fixedFee ?? "",
                        variableFee: obj?.variableFee ?? "",
                        slabs: obj?.slabs ?? [
                            {
                                rate: "",
                                minAum: "0",
                                maxAum: ""
                            }
                        ],
                        id: obj.id,
                        isActive: obj.isActive,
                        riaFeeDeclaration: obj?.riaFeeDeclaration ?? "",
                        selectedDeclarationId: parentId,
                        selectedSubPointId: subPointId,
                        ...((application?.isHeadOfFamily === "N" && familyFeeType) ? { feeType: familyFeeType } : {})
                    };
                })) : [{
                    ...initialRiaFeeObj,
                    applicationId: application?.id ?? "",
                    ...((application?.isHeadOfFamily === "N" && familyFeeType) ? { feeType: familyFeeType } : {})
                }],

        })
    }

    useEffect(() => {
        setFormData()
    }, [application])

    const handleSubmit = async (values: Values) => {
        try {
            for (const fee of values.RiaFees) {
                if (!fee.selectedDeclarationId) {
                    dispatch(showError("Please select the declaration"));
                    return;
                }
                if (fee.selectedDeclarationId === "2" && !fee.selectedSubPointId) {
                    dispatch(showError("Please select the declaration"));
                    return;
                }
            }

            const updatedRiaFees = (values?.RiaFees ?? []).map(fee => {
                let declarationId = "";
                if (fee.selectedDeclarationId === "1") {
                    declarationId = "1";
                } else if (fee.selectedDeclarationId === "2" && fee.selectedSubPointId) {
                    declarationId = fee.selectedSubPointId;
                }
                const { selectedDeclarationId: _declId, selectedSubPointId: _subId, ...rest } = fee;
                return {
                    ...rest,
                    riaFeeDeclaration: declarationId,
                };
            });

            setLoading(true)

            const {
                id,
                currentStep,
                applicationType,
                status,
                applicant1ReferenceId,
                applicationNumber
            } = application || {};

            const checkApplication = applicationComparison(
                {
                    ...application,
                },
                {
                    ...application,
                    RiaFees: updatedRiaFees,
                    currentStep:
                        applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                            ? !!currentStep && currentStep > 8
                                ? currentStep
                                : Number(currentStep) + 1
                            : !!currentStep && currentStep > 10
                                ? currentStep
                                : Number(currentStep) + 1,
                }
            );

            const isSaveLater = values?.saveType !== 'save and proceed';

            let currentStatus = status

            if (
                !isModuleAllowed({ value: (role ?? ""), compareArr: [USER_ROLES.AMCAPPROVER] }) &&
                status === 'sent_to_amc_approver'
            ) {
                currentStatus = 'draft'
            }

            if (id && !checkApplication) {
                setLoading(true);
                const response = await dispatch(
                    updateApplication({
                        body: {
                            ...application,
                            RiaFees: updatedRiaFees,
                            status: currentStatus,
                            currentStep: applicationType === APPLICATION_TYPE.NON_INDIVIDUAL ? 9 : 11,
                        },
                        applicationId: id,
                        ...(isSaveLater && { toastMessage: '' }),
                    })
                );
                if (!response) {
                    setLoading(false);
                    return;
                }
                !isSaveLater
                    ? history.push('document-details', { id, applicant1ReferenceId })
                    : history.push(saveForLater(role, id, applicant1ReferenceId));
            } else if (checkApplication) {
                if (isSaveLater) {
                    enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
                        variant: 'success',
                        autoHideDuration: 3000,
                    });
                }
                !isSaveLater
                    ? history.push('document-details', { id, applicant1ReferenceId })
                    : history.push(saveForLater(role, id, applicant1ReferenceId));
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            {isFeeTypeLoading && <LinearProgress />}
            {!isFeeTypeLoading && (
                <Formik
                    innerRef={formikRef}
                    initialValues={initialFormikValues}
                    validate={(values: Values) => {
                        try {
                            validateYupSchema(values, riaFeeValidationSchema(locale, currencyCode), true, values);
                        } catch (e) {
                            return yupToFormErrors(e);
                        }
                    }}
                    enableReinitialize
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, setValues, setFieldTouched }) => {
                        return (
                            <Form>
                                <Grid container spacing={2}>
                                    {Array.isArray(values?.RiaFees) && values.RiaFees.map((currentFee, feeIndex) => {
                                        const handleFeeTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                                            const newFeeType = event.target.value;
                                            const initialRiaFeeData = initialFormikValues.RiaFees[feeIndex]

                                            setValues(
                                                {
                                                    ...values,
                                                    RiaFees: Array.isArray(values?.RiaFees) ? values.RiaFees.map((feeObj, idx) =>
                                                        idx === feeIndex
                                                            ? {
                                                                ...feeObj,
                                                                feeType: newFeeType,
                                                                fixedFee: initialRiaFeeData.fixedFee,
                                                                variableFee: initialRiaFeeData.variableFee,
                                                                slabs: initialRiaFeeData.slabs,
                                                            }
                                                            : feeObj
                                                    ) : [],
                                                },
                                                false
                                            );
                                        };

                                        return (
                                            <Grid
                                                item
                                                xs={12}
                                                key={feeIndex}
                                                sx={{
                                                    mb: feeIndex < (values.RiaFees.length - 1) ? "2rem" : 0
                                                }}
                                            >
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} lg={6} sx={{ mt: "0.5rem", mb: "1.5rem" }}>
                                                        <TextDatePicker
                                                            label="Effective Agreement Date *"
                                                            inputLabelStyles={{
                                                                transform: 'unset',
                                                                fontSize: 14,
                                                                fontWeight: 500,
                                                                color: 'rgba(0,0,0,0.7)',
                                                            }}
                                                            placeholder='Enter Effective Agreement Date'
                                                            name={`RiaFees.${feeIndex}.agreementDate`}
                                                            maxDate={new Date()}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Typography
                                                    variant='body_500'
                                                    sx={{
                                                        color: theme.palette.text.primary,
                                                        mb: "1.5rem"
                                                    }}
                                                >
                                                    Fee Type
                                                </Typography>
                                                <RadioGroup
                                                    row
                                                    value={currentFee.feeType}
                                                    onChange={handleFeeTypeChange}
                                                    sx={{ flexWrap: "wrap", gap: "15px", mb: "2rem" }}
                                                >
                                                    {Array.isArray(FEE_TYPES) && FEE_TYPES.map((item) => {
                                                        const isChecked = currentFee.feeType === item.value;

                                                        return (
                                                            <FormControlLabel
                                                                key={item.value}
                                                                value={item.value}
                                                                label={item.label}
                                                                control={
                                                                    <Radio
                                                                        disableRipple
                                                                        size="small"
                                                                        sx={{
                                                                            padding: "5px",
                                                                            color: theme.palette.text.secondary,
                                                                            '&.Mui-checked': {
                                                                                color: theme.palette.primary[500],
                                                                            },
                                                                            ':hover': { background: 'none' },
                                                                            "&.Mui-disabled": {
                                                                                color: `${theme.palette.text.secondary}B3`,
                                                                                opacity: 0.5
                                                                            }
                                                                        }}
                                                                    />
                                                                }
                                                                sx={{
                                                                    padding: "4px 15px 4px 8px",
                                                                    borderRadius: "10px",
                                                                    marginLeft: 0,
                                                                    marginRight: 0,
                                                                    columnGap: "8px",
                                                                    backgroundColor: isChecked
                                                                        ? `${theme.palette.primary[500]}1A`
                                                                        : theme.palette.common.white,
                                                                    border: isChecked
                                                                        ? `1px solid ${theme.palette.primary[500]}`
                                                                        : `0.5px solid ${theme.palette.text.secondary}80`,
                                                                    '.MuiFormControlLabel-label': {
                                                                        ...theme.typography.body_500,
                                                                        color: isChecked
                                                                            ? theme.palette.primary[500]
                                                                            : theme.palette.text.secondary,
                                                                        cursor: 'pointer',
                                                                        "&.Mui-disabled": {
                                                                            pointerEvents: "auto",
                                                                            cursor: "not-allowed",
                                                                            color: `${theme.palette.text.secondary}B3`
                                                                        }
                                                                    },
                                                                }}
                                                                disabled={values.isHeadOfFamily !== 'Y'}
                                                            />
                                                        );
                                                    })}
                                                </RadioGroup>
                                                {currentFee.feeType === FEE_TYPE_VALUES.FIXED_FEE && (
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} lg={6}>
                                                            <MFTextField
                                                                name={`RiaFees.${feeIndex}.fixedFee`}
                                                                label="Annual Fixed Fee Amount (₹)*"
                                                                onKeyDown={allowDecimalNumbers}
                                                            />
                                                            {currentFee.fixedFee && (
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight: 500,
                                                                        fontSize: 14,
                                                                    }}
                                                                >
                                                                    {currencyConversion(locale, Number(currentFee.fixedFee))}
                                                                </Typography>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                )}
                                                {currentFee.feeType === FEE_TYPE_VALUES.AUA_FEE_VARIABLE && (
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} lg={6}>
                                                            <MFTextField
                                                                name={`RiaFees.${feeIndex}.variableFee`}
                                                                label="Fee percentage P.A (%)*"
                                                                onKeyDown={allowDecimalNumbers}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                )}
                                                {currentFee.feeType === FEE_TYPE_VALUES.AUA_FEE_SLAB_WISE && (
                                                    <FieldArray name={`RiaFees.${feeIndex}.slabs`}>
                                                        {(arrayHelpers) => (
                                                            <Box
                                                                sx={{
                                                                    maxHeight: "400px",
                                                                    overflowY: "auto",
                                                                    pr: "0.5rem",
                                                                }}
                                                            >
                                                                {Array.isArray(currentFee?.slabs) && currentFee.slabs.map((_slab: SlabObj, slabIndex: number) => (
                                                                    <Box key={slabIndex}>
                                                                        <Box sx={{ mb: "1.5rem" }}>
                                                                            <Box
                                                                                sx={{
                                                                                    display: "flex",
                                                                                    alignItems: "flex-start",
                                                                                    gap: "1rem",
                                                                                }}
                                                                            >
                                                                                <Grid container columnSpacing={2} rowSpacing={1.5} alignItems="flex-start" sx={{ flex: 1 }}>
                                                                                    <Grid item xs={12} sm={12} md={12} lg={6}>
                                                                                        <MFTextField
                                                                                            name={`RiaFees.${feeIndex}.slabs.${slabIndex}.minAum`}
                                                                                            label="Minimum AUM (₹)*"
                                                                                            placeholder="Enter Minimum AUM"
                                                                                            onKeyDown={allowDecimalNumbers}
                                                                                            disabled
                                                                                        />
                                                                                        {_slab?.minAum && (
                                                                                            <Typography
                                                                                                sx={{
                                                                                                    fontWeight: 500,
                                                                                                    fontSize: 14,
                                                                                                }}
                                                                                            >
                                                                                                {currencyConversion(locale, Number(_slab.minAum))}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Grid>
                                                                                    <Grid item xs={12} sm={12} md={12} lg={6}>
                                                                                        <MFTextField
                                                                                            name={`RiaFees.${feeIndex}.slabs.${slabIndex}.maxAum`}
                                                                                            label="Maximum AUM (₹)*"
                                                                                            placeholder="Enter Maximum AUM"
                                                                                            onKeyDown={allowDecimalNumbers}
                                                                                            disabled={slabIndex < currentFee.slabs.length - 1}
                                                                                        />
                                                                                        {_slab?.maxAum && (
                                                                                            <Typography
                                                                                                sx={{
                                                                                                    fontWeight: 500,
                                                                                                    fontSize: 14,
                                                                                                }}
                                                                                            >
                                                                                                {currencyConversion(locale, Number(_slab.maxAum))}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Grid>
                                                                                    <Grid item xs={12} sm={12} md={12} lg={6}>
                                                                                        <MFTextField
                                                                                            name={`RiaFees.${feeIndex}.slabs.${slabIndex}.rate`}
                                                                                            label="Fee percentage P.A (%)*"
                                                                                            placeholder="Enter Fee percentage P.A (%)"
                                                                                            onKeyDown={allowDecimalNumbers}
                                                                                        />
                                                                                    </Grid>
                                                                                </Grid>
                                                                                {(currentFee.slabs.length > 1) && (
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={() => arrayHelpers.remove(slabIndex)}
                                                                                        sx={{
                                                                                            color: theme.palette.error.main,
                                                                                            alignSelf: "flex-start",
                                                                                            mt: "0.5rem",
                                                                                            "&:hover": {
                                                                                                backgroundColor: `${theme.palette.error.main}1A`,
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <DeleteIcons />
                                                                                    </IconButton>
                                                                                )}
                                                                            </Box>
                                                                        </Box>
                                                                        {(currentFee.slabs.length > 1 && slabIndex < currentFee.slabs.length - 1) && (
                                                                            <Divider sx={{ borderColor: theme.palette.grey[500], my: "1rem" }} />
                                                                        )}
                                                                    </Box>
                                                                ))}
                                                                <Button
                                                                    variant="outlined"
                                                                    onClick={async () => {
                                                                        const lastSlabIndex = currentFee.slabs.length - 1;
                                                                        const slabBasePath = `RiaFees.${feeIndex}.slabs.${lastSlabIndex}`;

                                                                        // Touch all fields of the last slab to trigger validation messages
                                                                        await setFieldTouched(`${slabBasePath}.rate`, true, true);
                                                                        await setFieldTouched(`${slabBasePath}.minAum`, true, true);
                                                                        await setFieldTouched(`${slabBasePath}.maxAum`, true, true);

                                                                        // Validate last slab fields using formikRef to get the latest errors after touch
                                                                        // We need a small delay to let Formik revalidate after touching
                                                                        setTimeout(() => {
                                                                            const currentErrors = formikRef.current?.errors;
                                                                            const slabErrors = (currentErrors?.RiaFees as Array<{ slabs?: Array<{ rate?: string; minAum?: string; maxAum?: string }> }> | undefined)?.[feeIndex]?.slabs;

                                                                            // Check if the last slab has any validation errors
                                                                            // Only add a new slab if the last slab passes all validations
                                                                            const lastSlabErrors = Array.isArray(slabErrors)
                                                                                ? slabErrors[lastSlabIndex]
                                                                                : undefined;

                                                                            const hasErrors = lastSlabErrors &&
                                                                                (lastSlabErrors.rate || lastSlabErrors.minAum || lastSlabErrors.maxAum);

                                                                            if (hasErrors) {
                                                                                // Do not add a new slab — validation errors exist on the last slab
                                                                                return;
                                                                            }

                                                                            // All validations passed on the last slab — add a new slab
                                                                            const lastSlab = currentFee.slabs[lastSlabIndex];
                                                                            const newMinAum = lastSlab?.maxAum
                                                                                ? `${(Number(lastSlab.maxAum) + 1)}`
                                                                                : "";
                                                                            arrayHelpers.push({
                                                                                rate: "",
                                                                                minAum: newMinAum,
                                                                                maxAum: "",
                                                                            });
                                                                        }, 0);
                                                                    }}
                                                                    sx={{
                                                                        borderRadius: "10px",
                                                                        borderColor: theme.palette.primary[500],
                                                                        color: theme.palette.primary[500],
                                                                        ...theme.typography.body_500,
                                                                        px: "1.5rem",
                                                                        py: "0.5rem",
                                                                        mt: "1rem",
                                                                        "&:hover": {
                                                                            borderColor: theme.palette.primary[500],
                                                                            backgroundColor: `${theme.palette.primary[500]}0D`,
                                                                        },
                                                                    }}
                                                                >
                                                                    + Add More Slab
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </FieldArray>
                                                )}
                                            </Grid>
                                        );
                                    })}
                                    <Grid item xs={12} my={2}>
                                        <Divider sx={{ borderColor: theme.palette.grey[500] }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography
                                            id="availing-investment-advisory-services-title"
                                            variant="h2"
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            Availing Investment Advisory Services
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="body_500"
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            In this regard I/we would like to declare that currently (Please tick appropriate boxes) :
                                        </Typography>
                                    </Grid>
                                    {Array.isArray(values?.RiaFees) && values.RiaFees.map((currentFee, feeIndex) => (
                                        <Grid item xs={12} key={`declaration-${feeIndex}`}>
                                            <RadioGroup
                                                value={currentFee.selectedDeclarationId}
                                                onChange={(e) => {
                                                    const newId = e.target.value;
                                                    setFieldValue(`RiaFees.${feeIndex}.selectedDeclarationId`, newId);
                                                    if (newId !== "2") {
                                                        setFieldValue(`RiaFees.${feeIndex}.selectedSubPointId`, "");
                                                    }
                                                }}
                                                aria-labelledby="availing-investment-advisory-services-title"
                                            >
                                                {RIA_FEE_DECLARATION_POINTS.map((point) => (
                                                    <Box key={point.id} sx={{ mb: 2 }}>
                                                        <FormControlLabel
                                                            value={point.id}
                                                            control={
                                                                <Radio
                                                                    size="small"
                                                                    inputProps={{
                                                                        'aria-label': point.text,
                                                                        tabIndex: 0,
                                                                    }}
                                                                    sx={{
                                                                        padding: "0px 9px",
                                                                        mt: "3px",
                                                                        color: theme.palette.text.secondary,
                                                                        '&.Mui-checked': {
                                                                            color: theme.palette.primary[500],
                                                                        },
                                                                        '&.Mui-focusVisible': {
                                                                            outline: `1px solid ${theme.palette.primary[500]}`,
                                                                            outlineOffset: '-2px',
                                                                            borderRadius: '100%',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={
                                                                <Typography variant="body_500" sx={{ color: theme.palette.text.primary }}>
                                                                    {point.text}
                                                                </Typography>
                                                            }
                                                            sx={{ alignItems: "flex-start", mr: 0 }}
                                                        />
                                                        {point.subPoints && currentFee.selectedDeclarationId === point.id && (
                                                            <RadioGroup
                                                                value={currentFee.selectedSubPointId}
                                                                onChange={(e) => setFieldValue(`RiaFees.${feeIndex}.selectedSubPointId`, e.target.value)}
                                                                sx={{ pl: 4, mt: 1 }}
                                                                aria-label={`Sub-options for: ${point.text}`}
                                                            >
                                                                {point.subPoints.map((subPoint) => (
                                                                    <FormControlLabel
                                                                        key={subPoint.id}
                                                                        value={subPoint.id}
                                                                        control={
                                                                            <Radio
                                                                                size="small"
                                                                                inputProps={{
                                                                                    'aria-label': subPoint.text,
                                                                                    tabIndex: 0,
                                                                                }}
                                                                                sx={{
                                                                                    padding: "0px 9px",
                                                                                    mt: "3px",
                                                                                    color: theme.palette.text.secondary,
                                                                                    '&.Mui-checked': {
                                                                                        color: theme.palette.primary[500],
                                                                                    },
                                                                                    '&.Mui-focusVisible': {
                                                                                        outline: `1px solid ${theme.palette.primary[500]}`,
                                                                                        outlineOffset: '-2px',
                                                                                        borderRadius: '100%',
                                                                                    },
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={
                                                                            <Typography variant="body_500" sx={{ color: theme.palette.text.primary }}>
                                                                                {subPoint.text}
                                                                            </Typography>
                                                                        }
                                                                        sx={{ alignItems: "flex-start", mr: 0, mb: 1 }}
                                                                    />
                                                                ))}
                                                            </RadioGroup>
                                                        )}
                                                    </Box>
                                                ))}
                                            </RadioGroup>
                                        </Grid>
                                    ))}

                                    <Grid item xs={12} my={2} />
                                    <ProceedSaveLater
                                        saveLater={() => setFieldValue('saveType', 'save for later')}
                                        saveAndProceed={() => setFieldValue('saveType', 'save and proceed')}
                                        loader={isLoading}
                                        clickedButton={values.saveType}
                                        saveButtonText='Continue'
                                        showEndIcon={false}
                                    />
                                </Grid>
                            </Form>
                        );
                    }}
                </Formik>
            )}
        </>
    );
}
